import React, {
	memo,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import styled from "@emotion/styled";
import { generateGame, generateGames } from "./util/game";
import { fontSize, grid, spacing } from "../../../../styles/mixins";
import {
	fontRoslindaleDisplay,
	fontSurt,
	meta,
} from "../../../../styles/typography";
import { useGetResizer } from "../../../../contexts/Resizer";
import { motion, useTransform } from "framer-motion";
import StartingConditionCard from "./StartingConditionCard";
import Graph from "./Graph";
import { FIVE_YEARS } from "./constants";
import ChanceCard from "./ChanceCard";
import back from "./images/starting-conditions-back.png";
import { format } from "d3-format";
import House from "./House";
import Retire from "./Retire";
import GraphIntroduction from "./GraphIntroduction";
import { AgeContext } from "./AgeContext";
import { getStartingCardsFromGame } from "./util/getStartingCardsFromGame";

const Container = styled.div`
	width: 100%;
	height: 100vh;
	perspective: 1200px;
	overflow: hidden;
`;

const DeckContainer = styled(motion.div)`
	position: absolute;
	top: 0;
	left: 0;
	width: 1260px;
	max-width: calc(100% - 20px);
	height: 420px;
	perspective: 1200px;
`;
const TempDeck = styled(motion.div)`
	width: 270px;
	height: 390px;
	position: absolute;
	top: 0;
	left: 0;
	background-image: url(${back});
	background-size: cover;
`;

const MainScreenContainer = styled(motion.div)`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	${grid};
	grid-template-rows: auto 1fr;
	transform-style: preserve-3d;
`;

const MainScreenHeader = styled.div`
	grid-row: 1;
	margin-top: 60px;
	margin-bottom: 30px;
	grid-column: 2 / 30;
`;
const MainScreenTitle = styled.h2`
	${fontSize(2)};
	${fontSurt};
	margin-bottom: 10px;
	strong {
		${fontRoslindaleDisplay};
		font-weight: 400;
	}
`;

const MainScreenWealthLabel = styled.div`
	${meta};
	font-weight: 600;
	${spacing("marginBottom", "xxs")};
`;
const MainScreenWealth = styled.div`
	${meta};
	color: ${({ positive = true }) => (positive ? "green" : "red")};
`;

const EventsContainer = styled.div`
	grid-column: 34 / 47;
	grid-row: 1 / -1;
	position: relative;
	transform-style: preserve-3d;
`;
const Events = styled(motion.div)`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	transform-style: preserve-3d;
`;
const Event = styled.div`
	height: ${({ eventHeight }) => (eventHeight ? `${eventHeight}px` : null)};
	width: 100%;
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	transform-style: preserve-3d;
`;

// const CenterMarker = styled.div`
// 	position: absolute;
// 	top: 50%;
// 	transform: translateY(-50%);
// 	left: 0;
// 	width: 900px;
// 	height: 10px;
// 	border: 5px solid orange;
// `;

const formatWealth = format(",.0f");

const Wealth = ({ game }) => {
	let currentAge = useContext(AgeContext);
	const currentWealth = game
		? game.game[Math.max(Math.min(currentAge, 65), 18) - 18].wealth
		: 0;
	return (
		<MainScreenWealth positive={currentWealth >= 0}>
			{currentWealth < 0 ? "-" : ""}$
			{game && formatWealth(Math.abs(currentWealth))}
		</MainScreenWealth>
	);
};
export default memo(function Game({
	progress,
	age,
	containerBounds,
	initialState,
	game,
	setGame,
	otherGames,
	setOtherGames,
	setResetGame,
	setRetireHeight,
	retireHeight,
}) {
	const retireRef = useRef();
	const [screenBounds, setScreenBounds] = useState({
		width: 1440,
		height: 830,
	});
	const area = useGetResizer();

	useEffect(() => {
		setScreenBounds({
			width: window.innerWidth,
			height: window.innerHeight,
		});
	}, [area]);

	useEffect(() => {
		let rect = retireRef.current.getBoundingClientRect();
		setRetireHeight(rect.height);
	}, [area, game, setRetireHeight]);
	let cardContainerWidth = Math.min(1260, screenBounds.width - 20);
	let cardsXPosition = useTransform(
		progress,
		[0, 100],
		[
			screenBounds.width / 2 - 270 / 2,
			screenBounds.width / 2 - cardContainerWidth / 2,
		]
	);
	let cardsYPosition = useTransform(
		progress,
		[0, 100, 300, 400],
		[
			screenBounds.height + screenBounds.height * 0.25 + 390 / 2,
			screenBounds.height * 0.5 - 390 / 2,
			screenBounds.height * 0.5 - 390 / 2,
			screenBounds.height * 0.5 - 390 / 2 - screenBounds.height,
		]
	);

	let gutter = (cardContainerWidth - 270 * 4) / 3;
	let cardWidth = 270;
	let startPos1 = cardWidth + gutter;
	let startPos2 = cardWidth * 2 + gutter * 2;
	let startPos3 = cardWidth * 3 + gutter * 3;

	let mainScreenPosition = useTransform(
		progress,
		[300, 400],
		[screenBounds.height, 0]
	);
	let eventHeight = FIVE_YEARS;
	let additionalRetireHeight = Math.max(retireHeight - FIVE_YEARS, 0);
	let additionalYears = (additionalRetireHeight / eventHeight) * 5;
	let eventsProgression = useTransform(
		age,
		[20, 65, 65 + additionalYears],
		[
			screenBounds.height / 2 + eventHeight / 2,
			screenBounds.height / 2 - eventHeight / 2 - eventHeight * 9,
			screenBounds.height / 2 -
				eventHeight / 2 -
				eventHeight * 9 -
				additionalRetireHeight,
		]
	);
	let additionalGamesInput = useMemo(
		() => [65, 65 + additionalYears],
		[additionalYears]
	);
	const {
		privilege1,
		privilege1Enabled,
		privilege2,
		privilege2Enabled,
		context,
	} = getStartingCardsFromGame(game);

	const startOver = useCallback(() => {
		window.scrollTo(0, containerBounds.top);
		setResetGame(false);
		setGame(generateGame(initialState));
		setOtherGames(generateGames(initialState, 50));
	}, [containerBounds, setResetGame, setGame, setOtherGames, initialState]);

	return (
		<Container>
			<DeckContainer style={{ x: cardsXPosition, y: cardsYPosition }}>
				<TempDeck />
				<StartingConditionCard
					card={privilege1}
					enabled={privilege1Enabled}
					progress={progress}
					xInput={[100, 133]}
					xOutput={[0, startPos1]}
					zIndexInput={[100, 132, 133]}
					zIndexOutput={[5, 5, 1]}
					rotateYInput={[200, 233]}
					rotateYOutput={[180, 360]}
					zInput={[200, 216.5, 233]}
				/>
				<StartingConditionCard
					card={privilege2}
					enabled={privilege2Enabled}
					progress={progress}
					xInput={[133, 166]}
					xOutput={[0, startPos2]}
					zIndexInput={[133, 165, 166]}
					zIndexOutput={[4, 4, 1]}
					rotateYInput={[233, 266]}
					rotateYOutput={[180, 360]}
					zInput={[233, 249.5, 266]}
				/>
				<StartingConditionCard
					type={"context"}
					card={context}
					progress={progress}
					xInput={[166, 200]}
					xOutput={[0, startPos3]}
					zIndexInput={[166, 199, 200]}
					zIndexOutput={[3, 3, 1]}
					rotateYInput={[266, 300]}
					rotateYOutput={[180, 360]}
					zInput={[266, 272, 300]}
				/>
			</DeckContainer>
			<MainScreenContainer style={{ y: mainScreenPosition }}>
				<MainScreenHeader>
					<MainScreenTitle>
						A Game <strong>Called Life</strong>
					</MainScreenTitle>
					<MainScreenWealthLabel>TOTAL WEALTH</MainScreenWealthLabel>
					<Wealth game={game} />
				</MainScreenHeader>
				{game && (
					<Graph
						age={age}
						game={game}
						otherGames={otherGames}
						additionalGamesInput={additionalGamesInput}
					/>
				)}
				<EventsContainer>
					<Events style={{ y: eventsProgression }}>
						<Event eventHeight={eventHeight}>
							<GraphIntroduction />
						</Event>
						<Event eventHeight={eventHeight}>
							{game && <House state={game.game[25 - 18]} />}
						</Event>
						<Event eventHeight={eventHeight}>
							{game && (
								<ChanceCard
									age={age}
									chance={game.chanceCards[0]}
									currentAge={30}
								/>
							)}
						</Event>
						<Event eventHeight={eventHeight}>
							{game && <House state={game.game[35 - 18]} />}
						</Event>
						<Event eventHeight={eventHeight}>
							{game && (
								<ChanceCard
									age={age}
									currentAge={40}
									chance={game.chanceCards[1]}
								/>
							)}
						</Event>
						<Event eventHeight={eventHeight}>
							{game && <House state={game.game[45 - 18]} />}
						</Event>
						<Event eventHeight={eventHeight}>
							{game && (
								<ChanceCard
									age={age}
									currentAge={50}
									chance={game.chanceCards[2]}
								/>
							)}
						</Event>
						<Event eventHeight={eventHeight}>
							{game && <House state={game.game[55 - 18]} />}
						</Event>
						<Event eventHeight={eventHeight}>
							{game && (
								<ChanceCard
									age={age}
									currentAge={60}
									chance={game.chanceCards[3]}
								/>
							)}
						</Event>
						<Event eventHeight={false} ref={retireRef}>
							{game && (
								<Retire
									game={game}
									state={game.game[65 - 18]}
									houseState={game.game[55 - 18]}
									startOver={startOver}
								/>
							)}
						</Event>
					</Events>
				</EventsContainer>
			</MainScreenContainer>
		</Container>
	);
});
